import { profileResponseSchema, type ApiProfile } from '@contracts/system'
import { buildUrl } from '@/shared/api'
import type { RuntimeConfig } from './schema'

export { API_PROFILES, apiProfileSchema, type ApiProfile } from '@contracts/system'

/**
 * Public endpoint, relative to `config.apiBaseUrl` like every other one. The backend answers the
 * profile on its health endpoint too, but that one is outside the API prefix and the web server
 * of the dashboard forwards only the prefix, so a browser cannot reach it.
 */
export const API_PROFILE_PATH = '/system/profile'

/** The boot waits for this request, so it is given less time than an ordinary call. */
export const API_PROFILE_TIMEOUT_MS = 4_000

/**
 * Which deployment profiles serve each module. A module that is not listed is served by every
 * profile: sign-in and the interface texts are, and so will the shop-floor modules be. A
 * forgotten entry therefore lets the application start instead of refusing to boot over a module
 * the backend does serve.
 */
export const MODULE_PROFILES: Record<string, readonly ApiProfile[]> = {
  'model-training': ['training', 'all'],
  // Assumption: the models are served by the same backend as the training (design D1).
  'model-registry': ['training', 'all'],
}

export type ApiProfileResult =
  | { readonly ok: true; readonly profile: ApiProfile }
  | { readonly ok: false; readonly reason: string }

export interface ProfileMismatch {
  readonly message: string
  readonly issues: string[]
}

/** The enabled modules this profile does not serve, in the order they are configured. */
export function modulesNotServedBy(
  config: Pick<RuntimeConfig, 'enabledModules'>,
  profile: ApiProfile,
): string[] {
  return config.enabledModules.filter((id) => {
    const profiles = MODULE_PROFILES[id]
    return profiles !== undefined && !profiles.includes(profile)
  })
}

/**
 * What to show instead of the application, or `null` to start. A backend that could not be asked
 * is not a mismatch: unknown is not wrong, and a dashboard that refuses to boot until the API
 * answers would turn a restart into a blank screen.
 */
export function profileMismatch(
  config: Pick<RuntimeConfig, 'enabledModules'>,
  result: ApiProfileResult,
): ProfileMismatch | null {
  if (!result.ok) return null
  const unserved = modulesNotServedBy(config, result.profile)
  if (unserved.length === 0) return null
  return {
    message:
      `The backend serves the deployment profile "${result.profile}", which does not serve every ` +
      `module this dashboard is configured with. Correct enabledModules in config.json, or ` +
      `MESAI_PROFILE on the server.`,
    issues: unserved.map((id) => {
      const profiles = MODULE_PROFILES[id] ?? []
      return `${id}: served by ${profiles.join(' and ')}, not by ${result.profile}`
    }),
  }
}

/** Asks the backend which profile it serves. Never throws: every failure comes back as a reason. */
export async function fetchApiProfile(
  apiBaseUrl: string,
  fetchImpl: typeof fetch = fetch,
  timeoutMs: number = API_PROFILE_TIMEOUT_MS,
): Promise<ApiProfileResult> {
  const controller = new AbortController()
  const timer = setTimeout(() => {
    controller.abort()
  }, timeoutMs)
  try {
    const response = await fetchImpl(buildUrl(apiBaseUrl, API_PROFILE_PATH), {
      cache: 'no-store',
      headers: { Accept: 'application/json' },
      signal: controller.signal,
    })
    if (!response.ok) return { ok: false, reason: `HTTP ${response.status}` }
    const body: unknown = await response.json()
    const parsed = profileResponseSchema.safeParse(body)
    if (!parsed.success) return { ok: false, reason: 'the answer did not name a known profile' }
    return { ok: true, profile: parsed.data.profile }
  } catch (cause) {
    return { ok: false, reason: cause instanceof Error ? cause.message : String(cause) }
  } finally {
    clearTimeout(timer)
  }
}
